import { Capacitor } from '@capacitor/core';
import { TASK_PHOTO_MAX_EDGE, TASK_PHOTO_QUALITY, fittedSize } from '../shared/taskPhotoFit';
import { MAX_TASK_PHOTOS } from '../shared/projectTaskLimits';
import {
  ASSISTANT_IMAGE_MAX_EDGE,
  ASSISTANT_IMAGE_MAX_BYTES,
  ASSISTANT_JPEG_QUALITY,
} from '../shared/assistantPhoto';

function imageBox(image) {
  return {
    width: image.naturalWidth || image.width,
    height: image.naturalHeight || image.height,
  };
}

function loadImageElement(file) {
  const url = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Use a JPG or PNG photo.'));
    };
    image.src = url;
  });
}

async function loadImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch (error) {
      // HEIC from a desktop browser falls through to Image, then a clear message.
    }
  }
  return loadImageElement(file);
}

const ASSISTANT_PHOTO_MAX_EDGE = ASSISTANT_IMAGE_MAX_EDGE;
const ASSISTANT_PHOTO_QUALITY = ASSISTANT_JPEG_QUALITY;

async function compressImageFile(file, maxEdge, quality, filename) {
  if (!file) throw new Error('Choose a photo.');
  const image = await loadImage(file);
  const box = imageBox(image);
  const size = fittedSize(box.width, box.height, maxEdge);
  if (!size) throw new Error('Use a JPG or PNG photo.');
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not prepare that photo.');
  context.drawImage(image, 0, 0, size.width, size.height);
  if (typeof image.close === 'function') image.close();
  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });
  if (!blob) throw new Error('Could not prepare that photo.');
  return new File([blob], filename, { type: 'image/jpeg' });
}

/**
 * Shrink the longest edge to 1600px and write a JPEG.
 * iPhone photos are large; the private bucket stores this copy.
 */
export async function compressTaskPhoto(file) {
  return compressImageFile(file, TASK_PHOTO_MAX_EDGE, TASK_PHOTO_QUALITY, 'task-photo.jpg');
}

/**
 * A JPEG, PNG, or WebP that already fits is sent unchanged.
 * Larger photos are shrunk once, to 2000px on the long edge.
 * A browser that cannot decode the file, including many HEIC photos,
 * returns the original so the server converter still sees it.
 */
export async function prepareAssistantPhoto(file) {
  if (!file) return file;
  const type = String(file.type || '').toLowerCase();
  const passthrough = type === 'image/jpeg' || type === 'image/png' || type === 'image/webp' || type === 'image/gif';
  if (passthrough && file.size > 0 && file.size <= ASSISTANT_IMAGE_MAX_BYTES) {
    try {
      const image = await loadImage(file);
      const box = imageBox(image);
      if (typeof image.close === 'function') image.close();
      const longest = Math.max(box.width || 0, box.height || 0);
      if (longest > 0 && longest <= ASSISTANT_PHOTO_MAX_EDGE) return file;
    } catch (error) {
      return file;
    }
  }
  try {
    const base = String(file.name || 'assistant-photo').replace(/\.[a-z0-9]+$/i, '') || 'assistant-photo';
    const prepared = await compressImageFile(file, ASSISTANT_PHOTO_MAX_EDGE, ASSISTANT_PHOTO_QUALITY, `${base}.jpg`);
    if (prepared && prepared.size > 0) return prepared;
  } catch (error) {
    // Keep the original bytes.
  }
  return file;
}

async function fileFromWebPath(webPath) {
  const response = await fetch(webPath);
  const blob = await response.blob();
  const type = blob.type && blob.type.indexOf('image/') === 0 ? blob.type : 'image/jpeg';
  return new File([blob], 'task-photo.jpg', { type });
}

/**
 * On iPhone, the camera source takes one photo.
 * Returns null on the web so the caller can open a file input.
 */
export async function pickNativeTaskPhoto(options) {
  if (!Capacitor.isNativePlatform()) return null;
  const camera = await import('@capacitor/camera');
  const sourceName = options && options.source === 'camera' ? 'Camera' : 'Prompt';
  const photo = await camera.Camera.getPhoto({
    quality: Math.round(TASK_PHOTO_QUALITY * 100),
    width: TASK_PHOTO_MAX_EDGE,
    correctOrientation: true,
    resultType: camera.CameraResultType.Uri,
    source: camera.CameraSource[sourceName],
    promptLabelHeader: 'Add a task photo',
    promptLabelPhoto: 'Photo library',
    promptLabelPicture: 'Take photo',
  });
  if (!photo || !photo.webPath) throw new Error('No photo was selected.');
  return fileFromWebPath(photo.webPath);
}

/**
 * Photo library multi-select on iPhone and Android.
 * Returns null on the web so the caller can open a file input.
 * An empty array means the person closed the picker.
 */
export async function pickNativeTaskPhotos(limit) {
  if (!Capacitor.isNativePlatform()) return null;
  const camera = await import('@capacitor/camera');
  const cap = Math.max(1, Math.min(Number(limit) || MAX_TASK_PHOTOS, MAX_TASK_PHOTOS));
  if (typeof camera.Camera.pickImages !== 'function') {
    const one = await pickNativeTaskPhoto();
    return one ? [one] : [];
  }
  const picked = await camera.Camera.pickImages({
    quality: Math.round(TASK_PHOTO_QUALITY * 100),
    width: TASK_PHOTO_MAX_EDGE,
    correctOrientation: true,
    limit: cap,
  });
  const photos = picked && Array.isArray(picked.photos) ? picked.photos : [];
  const files = [];
  for (let index = 0; index < photos.length; index += 1) {
    const photo = photos[index];
    if (!photo || !photo.webPath) continue;
    files.push(await fileFromWebPath(photo.webPath));
  }
  return files;
}
